import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useStorage } from '~Components/Storage';
import {
  useBridgeApi,
  useIntegrationCheck,
  useInterval,
  useLicenses,
  useLogger,
  useSelector,
} from '~Hooks';
import { GetActiveOrders, GetActiveOrdersVariables } from './__queries__';
import { useIncomingOrderUtility } from '~Scenes/EventedKitchenDisplay/Hooks/useIncomingOrdersUtility';
import { ACCEPT_ORDER, FULL_ORDER, REJECT_ORDER } from '~Queries/orders';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import {
  AcceptPendingOrder,
  AcceptPendingOrderVariables,
  GetFullOrder,
  GetPendingOrders_venue_orders,
  RejectPendingOrder,
  RejectPendingOrderVariables,
} from '~Queries/__queries__';
import { DateTime } from 'luxon';
import AutoAcceptOrder from './AutoAcceptOrder';

const ACTIVE_ORDERS_QUERY = gql`
  query GetActiveOrders($venueId: ID!) {
    orders: allOrders(filter: { venue: { id: $venueId }, status: PENDING }) {
      id
      easyId
      status
      createdAt
    }
  }
`;

const GET_VENUE_PREP_TIME = gql`
  query GetVenuePrepTime($venueId: ID!) {
    Venue(id: $venueId) {
      id
      minimumPickupWaitTime
    }
  }
`;

const determineEstimatedTime = (
  order: GetPendingOrders_venue_orders,
  prepTimeMinutes: number,
): string => {
  // The closest time the order can be delivered, fallback 15 min
  const asapTime = DateTime.now().plus({ minutes: prepTimeMinutes || 15 });

  // If the customer has a desired time, check if we can use it
  if (order.desiredTime) {
    const desiredTime = DateTime.fromISO(order.desiredTime);

    if (!desiredTime.isValid) {
      return '';
    }
    // Check that the desired time isn't before the asap time
    if (desiredTime > asapTime) {
      return desiredTime.toISO();
    }
  }

  // If the user selects a different time with the x/- buttons we want to use it
  if (order.estimatedFinishTime && DateTime.fromISO(order.estimatedFinishTime) > asapTime) {
    return order.estimatedFinishTime;
  }

  // If the desired time is before the asap time, or there is no desired time
  return asapTime.toISO();
};

interface ContextType {
  pendingOrdersIds: string[];
  ordersToHandle: GetPendingOrders_venue_orders[];
  orderFetchingLoading: boolean;
  isAutoAccepting: boolean;
  acceptOrder: (order: GetPendingOrders_venue_orders) => Promise<void>;
  rejectOrder: (orderId: string, reason: string) => Promise<void>;
  getOrderById: (orderId: string) => Promise<GetPendingOrders_venue_orders | null>;
  showingIncomingModal: boolean;
  showModal: () => void;
  hideModal: () => void;
  prepTime: {
    time: number;
    loading: boolean;
    refetch: () => void;
  };
}

interface ProviderProps {
  children: React.ReactNode;
}

const Context = createContext<ContextType>({} as ContextType);

export const useIncomingAppOrders = () => useContext(Context);

export default function IncomingAppOrdersProvider({ children }: ProviderProps) {
  const logger = useLogger('incoming-app-orders');
  const licenses = useLicenses();
  const bridgeApi = useBridgeApi();
  const [storage] = useStorage();
  const storageRef = useRef(storage);
  const venueId = useSelector((state) => state.venueId);
  const [pendingOrderIds, setPendingOrderIds] = useState<string[]>([]);
  const [isAutoAccepting, setIsAutoAccepting] = useState(false);
  const [showingIncomingModal, setShowingIncomingModal] = useState(false);
  const [prepTime, setPrepTime] = useState(15); // Default to 15 minutes
  const acceptedOrderIdsRef = useRef<Set<string>>(new Set());

  // Pathname
  const pageHref = window?.location.href;

  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);

  // Used for manual accept
  const [orderFetchingLoading, setOrderFetchingLoading] = useState(false);
  const [ordersToHandle, setOrdersToHandle] = useState<GetPendingOrders_venue_orders[]>([]);

  const ordersQuery = useQuery<GetActiveOrders, GetActiveOrdersVariables>(ACTIVE_ORDERS_QUERY, {
    fetchPolicy: 'no-cache',
    variables: { venueId: venueId || '' },
    skip: !venueId || !licenses.hasApp,
    onError: (error) => {
      logger.error('Failed to fetch active orders', {
        errorMsg: error,
        queryDataAfterError: ordersQuery?.data?.orders?.map((o) => o.id) ?? 'undefined',
      });
    },
  });

  // Track consecutive poll failures to detect network issues
  const consecutiveFailuresRef = useRef(0);

  // Poll for active orders using interval instead of Apollo pollInterval (due to better control over the polling and avoid issues when there are network issues)
  useInterval(() => {
    if (!venueId || !licenses.hasApp) {
      return;
    }

    ordersQuery
      .refetch()
      .then((result) => {
        if (consecutiveFailuresRef.current > 0) {
          logger.info('Poll recovered after failures', {
            previousFailures: consecutiveFailuresRef.current,
            ordersFound: result.data?.orders?.length ?? 0,
            orderIds: result.data?.orders?.map((o) => o.id) ?? [],
          });
        }
        consecutiveFailuresRef.current = 0;
      })
      .catch((error) => {
        consecutiveFailuresRef.current += 1;
        logger.warn('Poll refetch failed', {
          consecutiveFailures: consecutiveFailuresRef.current,
          errorMsg: error?.message,
        });
      });
  }, 4000);

  // Refetch the prep time every 15 minutes
  const prepTimeQuery = useQuery(GET_VENUE_PREP_TIME, {
    variables: { venueId },
    skip: !venueId,
    fetchPolicy: 'no-cache',
    pollInterval: 60 * 1000 * 15,
  });

  useEffect(() => {
    if (!prepTimeQuery.data) {
      logger.warn('Failed to fetch minimumPickupWaitTime, using default', {
        errorMsg: prepTimeQuery.error,
      });
      return;
    }

    setPrepTime(prepTimeQuery.data?.Venue?.minimumPickupWaitTime || 15);
  }, [prepTimeQuery.data]);

  const client = useApolloClient();

  const hasSKVIntegration = useIntegrationCheck('SKATTEVERKET');
  const hasSKVDevice = storage.taxComplianceDeviceId;

  const orderUtility = useIncomingOrderUtility();

  const [acceptOrderMutation] = useMutation<AcceptPendingOrder, AcceptPendingOrderVariables>(
    ACCEPT_ORDER,
  );

  const [reject] = useMutation<RejectPendingOrder, RejectPendingOrderVariables>(REJECT_ORDER);

  // Fetch the full order from the server when accept is triggered
  const getOrderById = async (orderId: string): Promise<GetPendingOrders_venue_orders | null> => {
    const getFullOrderQuery = await client.query<GetFullOrder>({
      query: FULL_ORDER,
      variables: { orderId },
      fetchPolicy: 'no-cache',
    });

    if (getFullOrderQuery.error || !getFullOrderQuery.data?.order) {
      logger.warn(`Fetching full online order not found.`, {
        orderId,
        errorMsg: getFullOrderQuery.error ?? 'Unknown error',
      });
      return null;
    }

    return getFullOrderQuery.data.order;
  };

  useEffect(() => {
    // Skip adding pending orders if it is already in the acceptedOrderIdsRef.
    // Reason: Sometimes it takes a while for the backend to update the order status, and we are polling every 4 seconds,
    // so if there is a tiny mismatch between the backend and frontend, we might end up accepting the same order multiple times.
    const freshOrders = (ordersQuery.data?.orders || [])
      .map((o) => o.id)
      .filter((id) => !acceptedOrderIdsRef.current.has(id));

    if (freshOrders.length > 0) {
      logger.info('Processing ordersQuery.data effect', {
        rawOrders: ordersQuery.data?.orders?.map((o) => o.id) ?? 'undefined',
        rawOrdersLength: ordersQuery.data?.orders?.length ?? 'undefined',
        freshOrders,
        acceptedOrderIds: Array.from(acceptedOrderIdsRef.current),
        queryLoading: ordersQuery.loading,
        queryError: ordersQuery.error?.message ?? null,
      });
    }

    setPendingOrderIds(freshOrders);

    // Play sound if there are new orders and the setting is enabled and we are not in the KDS (since it handle the sound itself)
    if (
      freshOrders.length > 0 &&
      storage.onlinePlayNewOrderSound &&
      !pageHref.includes('kitchen-display')
    ) {
      bridgeApi.playSound();
    }

    if (freshOrders.length > 0 && storage.autoShowIncomingAppPopup) {
      setShowingIncomingModal(true);
    } else if (freshOrders.length === 0) {
      setShowingIncomingModal(false);
    }

    // Remove orders from acceptedOrderIdsRef that are no longer pending and therefor fully handled (to not make the acceptedOrderIds grow indefinitely)
    const acceptedOrderIds = Array.from(acceptedOrderIdsRef.current);
    const orderIdsToClear = acceptedOrderIds.filter(
      (id) => !ordersQuery.data?.orders.some((o) => o.id === id),
    );
    orderIdsToClear.forEach((id) => {
      acceptedOrderIdsRef.current.delete(id);
    });
  }, [ordersQuery.data?.orders]);

  // --------------- AUTO-ACCEPT TRACKING -------------------
  // Orders currently being auto-accepted by individual AutoAcceptOrder components
  const [autoAcceptingOrderIds, setAutoAcceptingOrderIds] = useState<string[]>([]);

  const onOrderAutoAccepted = useCallback((orderId: string) => {
    acceptedOrderIdsRef.current.add(orderId);
    setPendingOrderIds((prev) => prev.filter((id) => id !== orderId));
    setAutoAcceptingOrderIds((prev) => prev.filter((id) => id !== orderId));
  }, []);

  const onOrderAutoAcceptGaveUp = useCallback((orderId: string) => {
    logger.warn('Auto-accept gave up for order, removing from auto-accept queue', { orderId });
    setAutoAcceptingOrderIds((prev) => prev.filter((id) => id !== orderId));
  }, []);

  // --------------- ACCEPTING ONLINE ORDERS -------------------
  // Triggered from both auto accept and manual accept
  const acceptOrder = async (order: GetPendingOrders_venue_orders) => {
    logger.info(`Starting accept online order flow ${order.easyId}`, {
      orderId: order.id,
    });

    const refetchedOrder = await getOrderById(order.id);

    if (refetchedOrder?.status !== ORDER_STATUS.PENDING) {
      logger.info(`The order ${order.easyId} is already accepted, skipping`, {
        orderId: order.id,
        orderStatus: refetchedOrder?.status,
      });

      acceptedOrderIdsRef.current.add(order.id);
      setPendingOrderIds((prev) => prev.filter((id) => id !== order.id));
      return;
    }

    // --- Re-fetch fetch minimumPickupWaitTime in case it has changed ---
    let prepTimeMinutes = prepTime; // default fallback to the last known prep time
    try {
      const venuePrepTimeQuery = await client.query({
        query: GET_VENUE_PREP_TIME,
        variables: { venueId },
        fetchPolicy: 'no-cache',
      });

      if (
        venuePrepTimeQuery.data?.Venue?.minimumPickupWaitTime &&
        prepTime !== venuePrepTimeQuery.data?.Venue?.minimumPickupWaitTime
      ) {
        setPrepTime(venuePrepTimeQuery.data?.Venue?.minimumPickupWaitTime);
      }

      logger.info('Fetched minimumPickupWaitTime', {
        minimumPickupWaitTime: venuePrepTimeQuery.data?.Venue?.minimumPickupWaitTime,
      });

      prepTimeMinutes = venuePrepTimeQuery.data?.Venue?.minimumPickupWaitTime || prepTime; // fallback to the last known prep time
    } catch (error) {
      logger.warn('Failed to fetch minimumPickupWaitTime, using default', { errorData: error });
    }

    // -------------- PREPARE DATA ---------------
    // Will get converted to ISOString before passing to mutation
    const estimatedFinishTime = determineEstimatedTime(order, prepTimeMinutes);

    // If the order has a table, it is a table order and should be completed instantly
    // And if the setting is set to complete all orders instantly
    const newStatus =
      order.tableId || storageRef.current.completeAllAppOrdersInstantly
        ? ORDER_STATUS.COMPLETED
        : ORDER_STATUS.ACCEPTED;

    try {
      logger.info(`Attempting to accept online order ${order.easyId}`, {
        orderId: order.id,
        orderInfo: {
          orderEstimatedFinishTime: order.estimatedFinishTime,
          adjustedFinishTime: estimatedFinishTime,
          desiredTime: order.desiredTime,
          prepTime: prepTimeMinutes,
          newStatus,
        },
      });

      const result = await acceptOrderMutation({
        variables: {
          id: order.id,
          status: newStatus,
          estimatedFinishTime: estimatedFinishTime,
        },
      });

      // Check the response if OK
      if (result.data?.updateOrder.status === newStatus) {
        logger.info(`Successfully accepted online order ${order.easyId}`, {
          orderId: order.id,
          estimatedFinishTime,
          newStatus,
        });

        // Removes from pending
        setPendingOrderIds((prev) => prev.filter((id) => id !== order.id));

        // If there is only one pending order, we hide the modal since we are done
        if (pendingOrderIds.length === 1) {
          setShowingIncomingModal(false);
        }

        // TRIGGER POST ACTIONS
        postAcceptActions({
          ...order,
          estimatedFinishTime: estimatedFinishTime,
          status: newStatus,
        });
        return;
      }
    } catch (error) {
      console.error(error);
      logger.warn(`Failed to accept online order ${order.easyId}`, {
        orderId: order.id,
        order,
        errorMsg: error,
      });

      throw error;
    }
  };

  // --------------- REJECT ONLINE ORDERS -------------------
  const rejectOrder = async (orderId: string, reason: string) => {
    try {
      logger.info('Attempting to reject online order', {
        orderId,
        reason,
      });

      const res = await reject({
        variables: { id: orderId, rejectReason: reason },
      });

      if (res.data?.setOrderRejected.success) {
        logger.info(`Successfully rejected online order`, {
          orderId,
          reason,
        });

        // Removes from pending
        setPendingOrderIds((prev) => prev.filter((id) => id !== orderId));

        // If there is only one pending order, we hide the modal since we are done
        if (pendingOrderIds.length === 1) {
          setShowingIncomingModal(false);
        }
      }

      if (res.data?.setOrderRejected.error) {
        throw new Error(res.data?.setOrderRejected.error ?? 'Unknown error');
      }
    } catch (error) {
      logger.error(`Failed to reject order`, {
        orderId,
        reason,
        errorMsg: error,
      });

      throw error;
    }
  };

  // --------------- FETCHING FULL ORDERS (for manual accept modal) -------------------
  const fetchOrders = async () => {
    setOrderFetchingLoading(true);

    const fullOrders = await Promise.all(pendingOrderIds.map((orderId) => getOrderById(orderId)));
    setOrderFetchingLoading(false);
    setOrdersToHandle(fullOrders.filter((o) => o !== null));
  };

  useEffect(() => {
    if (showingIncomingModal) {
      fetchOrders();
    } else {
      setOrdersToHandle([]);
    }
  }, [showingIncomingModal, pendingOrderIds]);

  // --------------- AUTO ACCEPTING ONLINE ORDERS -------------------
  // Instead of a forEach loop, we track which orders need auto-accepting
  // and render an AutoAcceptOrder component for each one (see JSX below).
  // Each component handles its own retry logic independently.
  const isUsingBrowser = !window.ReactNativeWebView && !window.__DEV__;
  const shouldAutoAccept = storage.autoAcceptAppOrders && !isUsingBrowser;

  useEffect(() => {
    if (!shouldAutoAccept) return;

    if (pendingOrderIds.length > 0) {
      setIsAutoAccepting(true);
    } else {
      setTimeout(() => {
        setIsAutoAccepting(false);
      }, 3000);
    }

    logger.info('Auto-accept effect triggered', {
      pendingOrderIds,
      acceptedOrderIds: Array.from(acceptedOrderIdsRef.current),
      autoAcceptEnabled: storage.autoAcceptAppOrders,
    });

    // Add new pending orders to the auto-accept queue (if not already accepted or being processed)
    const newOrderIds = pendingOrderIds.filter(
      (id) => !acceptedOrderIdsRef.current.has(id),
    );

    if (newOrderIds.length > 0) {
      setAutoAcceptingOrderIds((prev) => {
        const combined = [...prev];
        newOrderIds.forEach((id) => {
          if (!combined.includes(id)) {
            combined.push(id);
          }
        });
        return combined;
      });

      // Remove from pending since AutoAcceptOrder components will handle them
      setPendingOrderIds((prev) => prev.filter((id) => !newOrderIds.includes(id)));
    }
  }, [pendingOrderIds, shouldAutoAccept]);

  // --------------- STALE ORDER DETECTION (for debugging) -------------------
  // If an order has been pending for over 2 minutes, something is wrong (auto-accept should have handled it)
  const STALE_ORDER_THRESHOLD_MS = 2 * 60 * 1000;
  useEffect(() => {
    const orders = ordersQuery.data?.orders || [];

    const staleOrders = orders.filter((order) => {
      const createdAt = DateTime.fromISO(order.createdAt);
      return (
        createdAt.isValid &&
        DateTime.now()
          .diff(createdAt)
          .as('milliseconds') > STALE_ORDER_THRESHOLD_MS
      );
    });

    if (staleOrders.length > 0) {
      logger.error('Stale pending orders detected - orders not being accepted', {
        staleOrderIds: staleOrders.map((o) => o.id),
        staleOrderAges: staleOrders.map((o) => ({
          id: o.id,
          easyId: o.easyId,
          ageMinutes: Math.round(
            DateTime.now()
              .diff(DateTime.fromISO(o.createdAt))
              .as('minutes'),
          ),
        })),
        autoAcceptEnabled: storage.autoAcceptAppOrders,
        pendingOrderIds,
        acceptedOrderIds: Array.from(acceptedOrderIdsRef.current),
      });
    }
  }, [ordersQuery.data?.orders]);

  // --------------- POST ACCEPT ACTIONS -------------------
  // Called after each accepted order
  const postAcceptActions = (order: GetPendingOrders_venue_orders) => {
    if (hasSKVIntegration && hasSKVDevice) {
      orderUtility.syncWithSKV(order);
      orderUtility.addToZReport(order);
    } else if (hasSKVDevice) {
      orderUtility.addToZReport(order);
    }

    orderUtility.subtractOrderInventory(order);
    orderUtility.sendToReduxState(order);
  };

  const contextValue = {
    pendingOrdersIds: pendingOrderIds,
    ordersToHandle: ordersToHandle,
    orderFetchingLoading,
    isAutoAccepting,
    acceptOrder,
    rejectOrder,
    getOrderById,
    showingIncomingModal,
    showModal: () => setShowingIncomingModal(true),
    hideModal: () => setShowingIncomingModal(false),
    prepTime: {
      time: prepTime,
      loading: prepTimeQuery.loading,
      refetch: () => prepTimeQuery.refetch(),
    },
  };

  return (
    <Context.Provider value={contextValue}>
      {shouldAutoAccept &&
        autoAcceptingOrderIds.map((orderId) => (
          <AutoAcceptOrder
            key={orderId}
            orderId={orderId}
            getOrderById={getOrderById}
            acceptOrder={acceptOrder}
            onAccepted={onOrderAutoAccepted}
            onGaveUp={onOrderAutoAcceptGaveUp}
          />
        ))}
      {children}
    </Context.Provider>
  );
}
